import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchAgeGroups, fetchCategories, fetchProducts, fetchReport, fetchSkills, createLeadCapture, emailReport } from "@/api";
import { ApiRequestError } from "@/api/client";
import type { ApiAgeGroup, ApiCategory, ApiProduct, ApiReport, ApiSkill } from "@/api/types";
import { getAgeRangeLabel, getCategoryUi } from "@/constants/ui";
import IconRenderer from "@/components/feature/IconRenderer";
import DevelopmentBar from "./components/DevelopmentBar";
import SkillProductCard from "./components/SkillProductCard";
import LeadCaptureModal from "@/components/feature/LeadCaptureModal";
import {
  createCheckoutSession,
  fetchCheckoutAddresses,
  fetchCheckoutCities,
  fetchCheckoutCountries,
  fetchCheckoutDistricts,
  fetchCheckoutProductOptions,
  fetchCheckoutShippingRates,
  fetchCheckoutStore,
  redirectToCheckout,
  saveCheckoutAddress,
  type CheckoutAddress,
  type CheckoutAddressPayload,
  type CheckoutCity,
  type CheckoutCountry,
  type CheckoutDistrict,
  type CheckoutProductOption,
  type CheckoutShippingRate,
} from "@/api/checkout";

const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

const loadGoogleMapsPlaces = (() => {
  let promise: Promise<void> | null = null;

  return () => {
    if (!googleMapsApiKey) return Promise.reject(new Error("google_maps_api_key_missing"));
    if (window.google?.maps?.places) return Promise.resolve();
    if (promise) return promise;

    promise = new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>("script[data-smart-growth-google-maps]");
      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener("error", () => reject(new Error("google_maps_load_failed")), { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsApiKey)}&libraries=places&language=ar&region=SA`;
      script.async = true;
      script.defer = true;
      script.dataset.smartGrowthGoogleMaps = "true";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("google_maps_load_failed"));
      document.head.appendChild(script);
    });

    return promise;
  };
})();

type GoogleAddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

const getGoogleAddressComponent = (components: GoogleAddressComponent[] = [], types: string[]) => {
  return components.find((component) => types.some((type) => component.types.includes(type)))?.long_name || "";
};

const normalizeLocationName = (value: string) => {
  return value
    .toLowerCase()
    .replace(/[\u064B-\u065F]/g, "")
    .replace(/\b(al|ar|as|ash|ad)\b/g, "")
    .replace(/حي|محافظة|منطقة|مدينة|district|dist|city|province|region|saudi arabia|kingdom of saudi arabia|السعودية|المملكة العربية السعودية/g, "")
    .replace(/[^a-z0-9\u0600-\u06FF]/g, "");
};

const findLocationMatch = <T extends { name: string; nameEn: string }>(items: T[], rawName: string) => {
  const normalizedName = normalizeLocationName(rawName);
  if (!normalizedName) return null;

  return items.find((item) => {
    const names = [item.name, item.nameEn].filter(Boolean);
    return names.some((name) => {
      const normalizedItemName = normalizeLocationName(name);
      return normalizedItemName === normalizedName ||
        normalizedItemName.includes(normalizedName) ||
        normalizedName.includes(normalizedItemName);
    });
  }) || null;
};

export default function Report() {
  const { t, i18n } = useTranslation("common");
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";
  const checkoutAddressSearchRef = useRef<HTMLInputElement | null>(null);

  const [showLeadModal, setShowLeadModal] = useState(false);
  const [ageGroups, setAgeGroups] = useState<ApiAgeGroup[]>([]);
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [skills, setSkills] = useState<ApiSkill[]>([]);
  const [products, setProducts] = useState<ApiProduct[]>([]);
  const [report, setReport] = useState<ApiReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [cartQuantities, setCartQuantities] = useState<Record<string, number>>({});
  const [cartSelectedOptions, setCartSelectedOptions] = useState<Record<string, Record<string, string>>>({});
  const [productOptions, setProductOptions] = useState<Record<string, CheckoutProductOption[]>>({});
  const [productOptionsLoading, setProductOptionsLoading] = useState<Record<string, boolean>>({});
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutSuccess, setCheckoutSuccess] = useState("");
  const [checkoutStoreName, setCheckoutStoreName] = useState("");
  const [checkoutRedirectMode, setCheckoutRedirectMode] = useState<"store_cart" | "direct_checkout" | "manual_payment_request" | "salla_order">("store_cart");
  const [checkoutAddresses, setCheckoutAddresses] = useState<CheckoutAddress[]>([]);
  const [selectedCheckoutAddressId, setSelectedCheckoutAddressId] = useState("");
  const [showNewCheckoutAddress, setShowNewCheckoutAddress] = useState(false);
  const [checkoutAddressForm, setCheckoutAddressForm] = useState<CheckoutAddressPayload>({
    label: "",
    recipientName: "",
    phoneNumber: "",
    countryId: "",
    cityId: "",
    districtId: "",
    districtName: "",
    address: "",
    addressLine: "",
    streetNumber: "",
    geoCoordinates: "",
    postalCode: "",
    isDefault: true,
  });
  const [shippingRates, setShippingRates] = useState<CheckoutShippingRate[]>([]);
  const [selectedShippingRateId, setSelectedShippingRateId] = useState("");
  const [shippingRatesLoading, setShippingRatesLoading] = useState(false);
  const [shippingScopeMissing, setShippingScopeMissing] = useState(false);
  const [checkoutCountries, setCheckoutCountries] = useState<CheckoutCountry[]>([]);
  const [checkoutCities, setCheckoutCities] = useState<CheckoutCity[]>([]);
  const [checkoutDistricts, setCheckoutDistricts] = useState<CheckoutDistrict[]>([]);
  const [checkoutCountriesLoading, setCheckoutCountriesLoading] = useState(false);
  const [checkoutCitiesLoading, setCheckoutCitiesLoading] = useState(false);
  const [checkoutDistrictsLoading, setCheckoutDistrictsLoading] = useState(false);
  const [checkoutAddressSearch, setCheckoutAddressSearch] = useState("");
  const [checkoutLocationLoading, setCheckoutLocationLoading] = useState(false);
  const [pendingCheckoutCityName, setPendingCheckoutCityName] = useState("");
  const [pendingCheckoutDistrictName, setPendingCheckoutDistrictName] = useState("");

  const assessmentData = useMemo(() => {
    const stored = sessionStorage.getItem("assessmentData");
    return stored ? JSON.parse(stored) : null;
  }, []);

  const hasValidReportId = Boolean(reportId && reportId !== "null" && reportId !== "undefined");
  const isPreviewMode = reportId === "preview";

  const ageGroupId = useMemo(() => {
    if (isPreviewMode) {
      return assessmentData?.ageGroupId as number | undefined;
    }
    return report?.ageGroupId;
  }, [isPreviewMode, assessmentData, report]);

  const checkedSkillsList = useMemo(() => {
    if (isPreviewMode) {
      return (assessmentData?.checkedSkills || []) as string[];
    }
    return (report?.checkedSkills || []) as string[];
  }, [isPreviewMode, assessmentData, report]);

  const checkedSet = useMemo(() => new Set(checkedSkillsList), [checkedSkillsList]);
  const cartStorageKey = useMemo(() => {
    if (!hasValidReportId || !ageGroupId) return null;
    return `smart-growth-report-cart:${reportId}:${ageGroupId}`;
  }, [hasValidReportId, reportId, ageGroupId]);
  const cartOptionsStorageKey = useMemo(() => {
    if (!cartStorageKey) return null;
    return `${cartStorageKey}:options`;
  }, [cartStorageKey]);

  const ageGroup = ageGroups.find((g) => g.id === ageGroupId);
  const ageRangeLabel = ageGroup ? getAgeRangeLabel(ageGroup.minMonths, ageGroup.maxMonths) : "";
  const ageSkills = useMemo(() => skills, [skills]);

  useEffect(() => {
    if (!hasValidReportId) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setHasError(false);

    const loadData = async () => {
      try {
        const [ageData, categoryData] = await Promise.all([
          fetchAgeGroups(i18n.language),
          fetchCategories(i18n.language),
        ]);

        if (!isMounted) return;
        setAgeGroups(ageData);
        setCategories(categoryData);

        if (isPreviewMode) {
          if (!assessmentData) {
            setHasError(true);
            setIsLoading(false);
            return;
          }
          setReport({
            id: "preview",
            childId: "preview",
            scorePhysical: assessmentData.scores?.physical || 0,
            scoreCognitive: assessmentData.scores?.cognitive || 0,
            scoreSocial: assessmentData.scores?.social || 0,
            createdAt: new Date().toISOString(),
            ageGroupId: assessmentData.ageGroupId,
            checkedSkills: assessmentData.checkedSkills,
          });
          setIsLoading(false);
        } else {
          const reportData = await fetchReport(reportId);
          if (!isMounted) return;
          if (!reportData) {
            setHasError(true);
          } else {
            setReport(reportData);
          }
          setIsLoading(false);
        }
      } catch (err) {
        if (!isMounted) return;
        setHasError(true);
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [reportId, i18n.language, isPreviewMode, assessmentData, hasValidReportId]);

  useEffect(() => {
    let isMounted = true;
    fetchCheckoutStore()
      .then((store) => {
        if (!isMounted) return;
        setCheckoutStoreName(store.displayName || "");
        setCheckoutRedirectMode(store.redirectMode || "store_cart");
      })
      .catch(() => {
        if (!isMounted) return;
        setCheckoutStoreName("");
        setCheckoutRedirectMode("store_cart");
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (checkoutRedirectMode !== "salla_order" || !hasValidReportId || isPreviewMode) return;
    let isMounted = true;

    fetchCheckoutAddresses(reportId)
      .then((addresses) => {
        if (!isMounted) return;
        setCheckoutAddresses(addresses);
        const defaultAddress = addresses.find((address) => address.isDefault) || addresses[0];
        setSelectedCheckoutAddressId((current) => current || defaultAddress?.id || "");
        setShowNewCheckoutAddress(addresses.length === 0);
      })
      .catch(() => {
        if (!isMounted) return;
        setCheckoutAddresses([]);
        setShowNewCheckoutAddress(true);
      });

    return () => {
      isMounted = false;
    };
  }, [checkoutRedirectMode, hasValidReportId, isPreviewMode, reportId]);

  useEffect(() => {
    if (checkoutRedirectMode !== "salla_order" || checkoutCountries.length > 0 || checkoutCountriesLoading) return;
    let isMounted = true;
    setCheckoutCountriesLoading(true);

    fetchCheckoutCountries()
      .then((countries) => {
        if (!isMounted) return;
        setCheckoutCountries(countries);
        const saudiArabia = countries.find((country) => country.code === "SA") || countries[0];
        if (saudiArabia && !checkoutAddressForm.countryId) {
          setCheckoutAddressForm((current) => ({ ...current, countryId: saudiArabia.id, cityId: "" }));
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setCheckoutCountries([]);
        setCheckoutError(t("checkoutLocationsLoadFailed"));
      })
      .finally(() => {
        if (!isMounted) return;
        setCheckoutCountriesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [checkoutCountries.length, checkoutRedirectMode, t]);

  useEffect(() => {
    if (checkoutRedirectMode !== "salla_order" || !checkoutAddressForm.countryId) {
      setCheckoutCities([]);
      setCheckoutDistricts([]);
      return;
    }

    let isMounted = true;
    setCheckoutCitiesLoading(true);
    fetchCheckoutCities(checkoutAddressForm.countryId)
      .then((cities) => {
        if (!isMounted) return;
        setCheckoutCities(cities);
        setCheckoutAddressForm((current) => ({
          ...current,
          cityId: current.cityId && cities.some((city) => city.id === current.cityId) ? current.cityId : "",
          districtId: current.cityId && cities.some((city) => city.id === current.cityId) ? current.districtId : "",
          districtName: current.cityId && cities.some((city) => city.id === current.cityId) ? current.districtName : "",
        }));
      })
      .catch(() => {
        if (!isMounted) return;
        setCheckoutCities([]);
        setCheckoutError(t("checkoutLocationsLoadFailed"));
      })
      .finally(() => {
        if (!isMounted) return;
        setCheckoutCitiesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [checkoutAddressForm.countryId, checkoutRedirectMode, t]);

  useEffect(() => {
    if (checkoutRedirectMode !== "salla_order" || !checkoutAddressForm.cityId) {
      setCheckoutDistricts([]);
      return;
    }

    let isMounted = true;
    setCheckoutDistrictsLoading(true);
    fetchCheckoutDistricts(checkoutAddressForm.cityId)
      .then((districts) => {
        if (!isMounted) return;
        setCheckoutDistricts(districts);
        setCheckoutAddressForm((current) => {
          const selectedDistrict = current.districtId
            ? districts.find((district) => district.id === current.districtId)
            : null;

          return {
            ...current,
            districtId: selectedDistrict ? selectedDistrict.id : "",
            districtName: selectedDistrict ? selectedDistrict.name : current.districtName,
          };
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setCheckoutDistricts([]);
        setCheckoutError(t("checkoutDistrictsLoadFailed"));
      })
      .finally(() => {
        if (!isMounted) return;
        setCheckoutDistrictsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [checkoutAddressForm.cityId, checkoutRedirectMode, t]);

  useEffect(() => {
    if (!pendingCheckoutCityName || checkoutCities.length === 0 || checkoutAddressForm.cityId) return;

    const city = findLocationMatch(checkoutCities, pendingCheckoutCityName);
    if (!city) return;

    setCheckoutAddressForm((current) => ({
      ...current,
      cityId: city.id,
      districtId: "",
    }));
    setPendingCheckoutCityName("");
  }, [checkoutAddressForm.cityId, checkoutCities, pendingCheckoutCityName]);

  useEffect(() => {
    const districtName = pendingCheckoutDistrictName || checkoutAddressForm.districtName;
    if (!districtName || checkoutDistricts.length === 0 || checkoutAddressForm.districtId) return;

    const district = findLocationMatch(checkoutDistricts, districtName);
    if (!district) return;

    setCheckoutAddressForm((current) => ({
      ...current,
      districtId: district.id,
      districtName: district.name || current.districtName,
    }));
    setPendingCheckoutDistrictName("");
  }, [checkoutAddressForm.districtId, checkoutAddressForm.districtName, checkoutDistricts, pendingCheckoutDistrictName]);

  useEffect(() => {
    if (!showNewCheckoutAddress || !googleMapsApiKey || !checkoutAddressSearchRef.current) return;
    let listener: { remove: () => void } | null = null;
    let isMounted = true;

    loadGoogleMapsPlaces()
      .then(() => {
        if (!isMounted || !checkoutAddressSearchRef.current || !window.google?.maps?.places) return;
        const autocomplete = new window.google.maps.places.Autocomplete(checkoutAddressSearchRef.current, {
          fields: ["name", "formatted_address", "geometry", "address_components"],
          componentRestrictions: { country: "sa" },
        });

        listener = autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          const lat = place.geometry?.location?.lat();
          const lng = place.geometry?.location?.lng();
          const coordinates = typeof lat === "number" && typeof lng === "number"
            ? `${lat.toFixed(6)},${lng.toFixed(6)}`
            : undefined;

          applyGoogleAddress(
            place.formatted_address || place.name || "",
            place.address_components || [],
            coordinates
          );
        });
      })
      .catch(() => {
        if (isMounted) setCheckoutError(t("checkoutGoogleMapsLoadFailed"));
      });

    return () => {
      isMounted = false;
      listener?.remove();
    };
  }, [showNewCheckoutAddress, t]);

  useEffect(() => {
    if (!ageGroupId) return;
    let isMounted = true;

    Promise.all([
      fetchSkills(ageGroupId, i18n.language),
      fetchProducts(ageGroupId, i18n.language),
    ])
      .then(([skillData, productData]) => {
        if (!isMounted) return;
        setSkills(skillData);
        setProducts(productData);
      })
      .catch(() => {
        if (!isMounted) return;
        setHasError(true);
      });

    return () => {
      isMounted = false;
    };
  }, [ageGroupId, i18n.language]);

  // The registration popup is triggered manually when the user clicks "Print" or "Save Report" in preview mode.

  // Calculate scores by category
  const scores = useMemo(() => {
    const result: Record<string, { score: number; total: number; percentage: number }> = {};
    categories.forEach((cat) => {
      const catSkills = ageSkills.filter((s) => s.category.key === cat.key);
      const mastered = catSkills.filter((s) => checkedSet.has(s.id)).length;
      const total = catSkills.length;
      
      let percentage = total > 0 ? Math.round((mastered / total) * 100) : 0;
      if (!isPreviewMode && report) {
        if (cat.key === "physical") percentage = report.scorePhysical;
        else if (cat.key === "cognitive") percentage = report.scoreCognitive;
        else if (cat.key === "social") percentage = report.scoreSocial;
      }

      result[cat.key] = {
        score: mastered,
        total,
        percentage,
      };
    });
    return result;
  }, [ageSkills, checkedSet, categories, isPreviewMode, report]);

  // Get missing skills by category
  const missingSkillsByCategory = useMemo(() => {
    const result: Record<string, ApiSkill[]> = {};
    categories.forEach((cat) => {
      result[cat.key] = ageSkills.filter((s) => s.category.key === cat.key && !checkedSet.has(s.id));
    });
    return result;
  }, [ageSkills, checkedSet, categories]);

  // Get mastered skills by category
  const masteredSkillsByCategory = useMemo(() => {
    const result: Record<string, ApiSkill[]> = {};
    categories.forEach((cat) => {
      result[cat.key] = ageSkills.filter((s) => s.category.key === cat.key && checkedSet.has(s.id));
    });
    return result;
  }, [ageSkills, checkedSet, categories]);

  // Get products for a specific skill (with fallback to category if none matched specifically)
  const getProductsForSkill = (skillId: string, categoryId: number) => {
    const skillProducts = products.filter((p) => p.skillIds?.includes(skillId) && p.stockQuantity > 0);
    if (skillProducts.length > 0) return skillProducts;
    return products.filter((p) => p.categoryId === categoryId && p.stockQuantity > 0);
  };

  const overallPercentage = categories.length > 0
    ? Math.round(categories.reduce((sum, cat) => sum + (scores[cat.key]?.percentage || 0), 0) / categories.length)
    : 0;

  const getStatus = (percentage: number) => {
    if (percentage >= 80) return { label: t("excellent"), color: "#2A9D8F" };
    if (percentage >= 60) return { label: t("good"), color: "#E9C46A" };
    if (percentage >= 40) return { label: t("developing"), color: "#F4A261" };
    return { label: t("needsAttention"), color: "#E8927C" };
  };

  const overallStatus = getStatus(overallPercentage);
  const recommendedProducts = useMemo(() => {
    const uniqueProducts = new Map<string, ApiProduct>();

    categories.forEach((cat) => {
      const missing = missingSkillsByCategory[cat.key] || [];
      missing.forEach((skill) => {
        const skillProducts = products.filter((p) => p.skillIds?.includes(skill.id) && p.stockQuantity > 0);
        const fallbackProducts = skillProducts.length > 0
          ? skillProducts
          : products.filter((p) => p.categoryId === cat.id && p.stockQuantity > 0);

        fallbackProducts.forEach((product) => {
          uniqueProducts.set(product.id, product);
        });
      });
    });

    return Array.from(uniqueProducts.values());
  }, [categories, missingSkillsByCategory, products]);

  const productById = useMemo(() => {
    return new Map(products.map((product) => [product.id, product]));
  }, [products]);

  const getSelectedProductStockLimit = (product: ApiProduct, selectedOptions = cartSelectedOptions[product.id] || {}) => {
    const selectedStocks = (productOptions[product.id] || [])
      .flatMap((option) => option.values.filter((value) => selectedOptions[option.id] === value.id))
      .map((value) => value.stockQuantity)
      .filter((stock): stock is number => typeof stock === "number");

    if (selectedStocks.length === 0) return product.stockQuantity;
    return Math.max(0, Math.min(product.stockQuantity, ...selectedStocks));
  };

  const selectedCartItems = useMemo(() => {
    return Object.entries(cartQuantities)
      .map(([productId, quantity]) => {
        const product = productById.get(productId);
        if (!product || quantity <= 0) return null;
        const selectedOptions = cartSelectedOptions[product.id] || {};
        const stockLimit = getSelectedProductStockLimit(product, selectedOptions);
        const options = (productOptions[product.id] || [])
          .filter((option) => selectedOptions[option.id])
          .map((option) => ({ id: option.id, value: [selectedOptions[option.id]] }));

        return { product, quantity: Math.min(quantity, stockLimit), stockLimit, options };
      })
      .filter((item): item is {
        product: ApiProduct;
        quantity: number;
        stockLimit: number;
        options: Array<{ id: string; value: string[] }>;
      } => Boolean(item));
  }, [cartQuantities, cartSelectedOptions, productById, productOptions]);

  const selectedProductIds = useMemo(() => {
    return selectedCartItems.map(({ product }) => product.id);
  }, [selectedCartItems]);

  const selectedItemsCount = selectedCartItems.reduce((sum, item) => sum + item.quantity, 0);
  const selectedTotal = selectedCartItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const selectedCheckoutAddress = useMemo(() => {
    return checkoutAddresses.find((address) => address.id === selectedCheckoutAddressId) || null;
  }, [checkoutAddresses, selectedCheckoutAddressId]);
  const selectedShippingRate = useMemo(() => {
    return shippingRates.find((rate) => rate.id === selectedShippingRateId) || null;
  }, [shippingRates, selectedShippingRateId]);
  const displayedShippingCost = checkoutRedirectMode === "salla_order"
    ? Number(selectedShippingRate?.amount || 0)
    : 0;
  const selectedGrandTotal = selectedTotal + displayedShippingCost;

  const missingRequiredOptions = useMemo(() => {
    return selectedCartItems.flatMap(({ product }) => {
      const selectedOptions = cartSelectedOptions[product.id] || {};
      return (productOptions[product.id] || [])
        .filter((option) => option.required && !selectedOptions[option.id])
        .map((option) => ({ product, option }));
    });
  }, [cartSelectedOptions, productOptions, selectedCartItems]);

  useEffect(() => {
    if (!cartStorageKey) return;
    const storedCart = sessionStorage.getItem(cartStorageKey);
    if (!storedCart) {
      setCartQuantities({});
      return;
    }

    try {
      const parsed = JSON.parse(storedCart) as Record<string, number>;
      setCartQuantities(parsed && typeof parsed === "object" ? parsed : {});
    } catch {
      setCartQuantities({});
    }
  }, [cartStorageKey]);

  useEffect(() => {
    if (!cartStorageKey) return;
    sessionStorage.setItem(cartStorageKey, JSON.stringify(cartQuantities));
  }, [cartStorageKey, cartQuantities]);

  useEffect(() => {
    if (!cartOptionsStorageKey) return;
    const storedOptions = sessionStorage.getItem(cartOptionsStorageKey);
    if (!storedOptions) {
      setCartSelectedOptions({});
      return;
    }

    try {
      const parsed = JSON.parse(storedOptions) as Record<string, Record<string, string>>;
      setCartSelectedOptions(parsed && typeof parsed === "object" ? parsed : {});
    } catch {
      setCartSelectedOptions({});
    }
  }, [cartOptionsStorageKey]);

  useEffect(() => {
    if (!cartOptionsStorageKey) return;
    sessionStorage.setItem(cartOptionsStorageKey, JSON.stringify(cartSelectedOptions));
  }, [cartOptionsStorageKey, cartSelectedOptions]);

  useEffect(() => {
    if (checkoutRedirectMode !== "salla_order") return;
    if (!selectedCheckoutAddress?.countryId || !selectedCheckoutAddress?.cityId) {
      setShippingRates([]);
      setSelectedShippingRateId("");
      setShippingScopeMissing(false);
      return;
    }

    let isMounted = true;
    setShippingRatesLoading(true);
    setCheckoutError("");
    setShippingScopeMissing(false);
    fetchCheckoutShippingRates({
      countryId: selectedCheckoutAddress.countryId,
      cityId: selectedCheckoutAddress.cityId,
      postalCode: selectedCheckoutAddress.postalCode,
    })
      .then((rates) => {
        if (!isMounted) return;
        setShippingRates(rates);
        setSelectedShippingRateId((current) => current && rates.some((rate) => rate.id === current)
          ? current
          : rates[0]?.id || "");
      })
      .catch((error) => {
        if (!isMounted) return;
        setShippingRates([]);
        setSelectedShippingRateId("");
        if (error instanceof ApiRequestError && error.code === "shippings_read_scope_required") {
          setShippingScopeMissing(true);
          setCheckoutError(t("shippingPermissionRequired"));
          return;
        }
        setCheckoutError(t("shippingRatesLoadFailed"));
      })
      .finally(() => {
        if (!isMounted) return;
        setShippingRatesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [checkoutRedirectMode, selectedCheckoutAddress, t]);

  useEffect(() => {
    selectedProductIds.forEach((productId) => {
      if (productOptions[productId] || productOptionsLoading[productId]) return;

      setProductOptionsLoading((current) => ({ ...current, [productId]: true }));
      fetchCheckoutProductOptions(productId)
        .then((options) => {
          setProductOptions((current) => ({ ...current, [productId]: options }));
          setCartSelectedOptions((current) => {
            const currentProductOptions = current[productId] || {};
            const defaults = options.reduce<Record<string, string>>((acc, option) => {
              if (currentProductOptions[option.id]) {
                acc[option.id] = currentProductOptions[option.id];
                return acc;
              }

              const defaultValue = option.values.find((value) => value.isDefault && !value.isOutOfStock)
                || option.values.find((value) => !value.isOutOfStock);
              if (defaultValue && !option.required) {
                acc[option.id] = defaultValue.id;
              }
              return acc;
            }, {});

            if (Object.keys(defaults).length === 0 && Object.keys(currentProductOptions).length === 0) {
              return current;
            }

            return {
              ...current,
              [productId]: {
                ...currentProductOptions,
                ...defaults,
              },
            };
          });
        })
        .catch(() => {
          setProductOptions((current) => ({ ...current, [productId]: [] }));
        })
        .finally(() => {
          setProductOptionsLoading((current) => ({ ...current, [productId]: false }));
        });
    });
  }, [productOptions, productOptionsLoading, selectedProductIds]);

  useEffect(() => {
    if (products.length === 0) return;
    setCartQuantities((current) => {
      let changed = false;
      const next: Record<string, number> = {};

      Object.entries(current).forEach(([productId, quantity]) => {
        const product = productById.get(productId);
        if (!product || product.stockQuantity <= 0) {
          changed = true;
          return;
        }

        const safeQuantity = Math.max(0, Math.min(product.stockQuantity, Math.floor(quantity)));
        if (safeQuantity !== quantity) changed = true;
        if (safeQuantity > 0) next[productId] = safeQuantity;
      });

      return changed ? next : current;
    });
  }, [productById, products.length]);

  const setProductQuantity = (product: ApiProduct, quantity: number) => {
    const parsedQuantity = Number.isFinite(quantity) ? Math.floor(quantity) : 0;
    const safeQuantity = Math.max(0, Math.min(product.stockQuantity, parsedQuantity));
    setCheckoutError("");
    setCheckoutSuccess("");
    setCartQuantities((current) => {
      const next = { ...current };
      if (safeQuantity <= 0) {
        delete next[product.id];
        setCartSelectedOptions((currentOptions) => {
          if (!currentOptions[product.id]) return currentOptions;
          const nextOptions = { ...currentOptions };
          delete nextOptions[product.id];
          return nextOptions;
        });
      } else {
        next[product.id] = safeQuantity;
      }
      return next;
    });
  };

  const addProductToCart = (product: ApiProduct) => {
    const currentQuantity = cartQuantities[product.id] || 0;
    setProductQuantity(product, currentQuantity + 1);
  };

  const addAllRecommendedProducts = () => {
    setCheckoutError("");
    setCheckoutSuccess("");
    setCartQuantities((current) => {
      const next = { ...current };
      recommendedProducts.forEach((product) => {
        if (product.stockQuantity <= 0) return;
        next[product.id] = Math.min(product.stockQuantity, Math.max(next[product.id] || 0, 1));
      });
      return next;
    });
  };

  const setProductOptionValue = (productId: string, optionId: string, valueId: string) => {
    setCheckoutError("");
    setCheckoutSuccess("");
    const product = productById.get(productId);
    const nextSelectedOptions = {
      ...(cartSelectedOptions[productId] || {}),
      [optionId]: valueId,
    };
    setCartSelectedOptions((current) => ({
      ...current,
      [productId]: nextSelectedOptions,
    }));

    if (product) {
      const stockLimit = getSelectedProductStockLimit(product, nextSelectedOptions);
      setCartQuantities((current) => {
        const currentQuantity = current[productId] || 0;
        if (currentQuantity <= stockLimit) return current;
        return { ...current, [productId]: stockLimit };
      });
    }
  };

  const updateCheckoutAddressForm = (field: keyof CheckoutAddressPayload, value: string | boolean) => {
    setCheckoutError("");
    setCheckoutSuccess("");
    setCheckoutAddressForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "countryId" ? { cityId: "" } : {}),
      ...(field === "countryId" || field === "cityId" ? { districtId: "", districtName: "" } : {}),
    }));
  };

  const updateCheckoutDistrict = (districtId: string) => {
    const district = checkoutDistricts.find((item) => item.id === districtId);
    setCheckoutError("");
    setCheckoutSuccess("");
    setCheckoutAddressForm((current) => ({
      ...current,
      districtId,
      districtName: district ? district.name : current.districtName,
    }));
  };

  const applyGoogleAddress = (
    formattedAddress: string,
    components: GoogleAddressComponent[] = [],
    coordinates?: string
  ) => {
    const streetNumber = getGoogleAddressComponent(components, ["street_number"]);
    const route = getGoogleAddressComponent(components, ["route"]);
    const districtName = getGoogleAddressComponent(components, [
      "neighborhood",
      "sublocality_level_1",
      "sublocality",
    ]);
    const cityName = getGoogleAddressComponent(components, ["locality"]) ||
      getGoogleAddressComponent(components, ["administrative_area_level_2"]) ||
      getGoogleAddressComponent(components, ["administrative_area_level_1"]);
    const postalCode = getGoogleAddressComponent(components, ["postal_code"]);
    const matchedCity = cityName ? findLocationMatch(checkoutCities, cityName) : null;
    const matchedDistrict = districtName && (!matchedCity || matchedCity.id === checkoutAddressForm.cityId)
      ? findLocationMatch(checkoutDistricts, districtName)
      : null;

    setCheckoutAddressSearch(formattedAddress);
    setPendingCheckoutCityName(matchedCity ? "" : cityName);
    setPendingCheckoutDistrictName(matchedDistrict ? "" : districtName);
    setCheckoutAddressForm((current) => ({
      ...current,
      address: formattedAddress || current.address,
      addressLine: route || current.addressLine,
      cityId: matchedCity?.id || current.cityId,
      districtId: matchedDistrict?.id || current.districtId,
      districtName: matchedDistrict?.name || districtName || current.districtName,
      streetNumber: streetNumber || current.streetNumber,
      postalCode: postalCode || current.postalCode,
      geoCoordinates: coordinates || current.geoCoordinates,
    }));
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setCheckoutError(t("checkoutLocationUnsupported"));
      return;
    }

    setCheckoutError("");
    setCheckoutLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = Number(position.coords.latitude.toFixed(6));
        const lng = Number(position.coords.longitude.toFixed(6));
        const coordinates = `${lat},${lng}`;

        if (!googleMapsApiKey) {
          setCheckoutAddressForm((current) => ({ ...current, geoCoordinates: coordinates }));
          setCheckoutLocationLoading(false);
          return;
        }

        loadGoogleMapsPlaces()
          .then(() => {
            const geocoder = new window.google!.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              const result = status === "OK" ? results?.[0] : null;
              applyGoogleAddress(
                result?.formatted_address || checkoutAddressForm.address || coordinates,
                result?.address_components || [],
                coordinates
              );
              setCheckoutLocationLoading(false);
            });
          })
          .catch(() => {
            setCheckoutAddressForm((current) => ({ ...current, geoCoordinates: coordinates }));
            setCheckoutLocationLoading(false);
          });
      },
      () => {
        setCheckoutError(t("checkoutLocationDenied"));
        setCheckoutLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  const handleSaveCheckoutAddress = async () => {
    if (!checkoutAddressForm.countryId.trim() || !checkoutAddressForm.cityId.trim() || !checkoutAddressForm.address.trim()) {
      setCheckoutError(t("checkoutAddressRequired"));
      return null;
    }

    try {
      const saved = await saveCheckoutAddress(checkoutAddressForm, !isPreviewMode && hasValidReportId ? reportId : undefined);
      setCheckoutAddresses((current) => {
        const next = saved.isDefault ? current.map((address) => ({ ...address, isDefault: false })) : current;
        return [saved, ...next.filter((address) => address.id !== saved.id)];
      });
      setSelectedCheckoutAddressId(saved.id);
      setShowNewCheckoutAddress(false);
      return saved;
    } catch (error) {
      console.error(error);
      setCheckoutError(t("checkoutAddressSaveFailed"));
      return null;
    }
  };

  const handleCheckout = async () => {
    if (selectedCartItems.length === 0) return;

    if (missingRequiredOptions.length > 0) {
      setCheckoutError(t("productOptionsRequired"));
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError("");
    setCheckoutSuccess("");
    try {
      let addressForOrder = selectedCheckoutAddress;

      if (checkoutRedirectMode === "salla_order") {
        if (showNewCheckoutAddress || !addressForOrder) {
          addressForOrder = await handleSaveCheckoutAddress();
        }

        if (!addressForOrder) {
          setCheckoutError(t("checkoutAddressRequired"));
          return;
        }

        if (shippingRatesLoading) {
          setCheckoutError(t("shippingRatesLoading"));
          return;
        }

        if (!shippingScopeMissing && (shippingRates.length === 0 || !selectedShippingRate)) {
          setCheckoutError(t("storeOrderShippingRequired"));
          return;
        }
      }

      const session = await createCheckoutSession(
        selectedCartItems.map(({ product, quantity, options }) => ({
          sallaProductId: product.id,
          quantity,
          identifierType: "id",
          options,
        })),
        {
          reportId: !isPreviewMode && hasValidReportId ? reportId : undefined,
          shippingAddressId: checkoutRedirectMode === "salla_order" ? addressForOrder?.id : undefined,
          shippingRateId: checkoutRedirectMode === "salla_order" ? selectedShippingRate?.id : undefined,
          shippingCompanyId: checkoutRedirectMode === "salla_order" ? selectedShippingRate?.companyId : undefined,
        },
      );
      if (session.data.redirectMode === "manual_payment_request") {
        setCheckoutSuccess(t("checkoutPaymentRequestCreated"));
        return;
      }
      if (session.data.redirectMode === "salla_order") {
        setCheckoutSuccess(t("checkoutSallaOrderCreated"));
        return;
      }
      const redirectUrl = session.data.redirectUrl || session.data.checkoutUrl;
      if (!redirectUrl) {
        throw new Error("missing_checkout_url");
      }
      redirectToCheckout(redirectUrl);
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : "";
      const errorCode = err instanceof ApiRequestError ? err.code : "";
      setCheckoutError(
        errorCode === "product_options_required" || message === "product_options_required"
          ? t("productOptionsRequired")
          : errorCode === "shipping_address_details_required" || message === "shipping_address_details_required"
            ? t("checkoutShippingAddressDetailsRequired")
          : errorCode === "store_order_shipping_required" || message === "store_order_shipping_required"
            ? t("storeOrderShippingRequired")
          : message && !["salla_checkout_failed", "alert.invalid_fields"].includes(message)
            ? message
            : t("checkoutPrepareFailed")
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleLeadSubmit = async (data: {
    parentName?: string;
    phone?: string;
    countryCode?: string;
    email?: string;
    password?: string;
    childName?: string;
    childDob?: string;
    childGender?: string;
    childId?: string;
  }) => {
    const parentPhoneWithCode = data.phone ? `${data.countryCode}${data.phone}` : undefined;

    if (data.parentName) sessionStorage.setItem("savedParentName", data.parentName);
    if (data.phone) sessionStorage.setItem("savedParentPhone", data.phone);
    if (data.countryCode) sessionStorage.setItem("savedParentCountryCode", data.countryCode);
    if (data.email) sessionStorage.setItem("savedParentEmail", data.email);

    return createLeadCapture({
      parent: data.parentName ? {
        name: data.parentName,
        phoneNumber: parentPhoneWithCode!,
        email: data.email!,
        password: data.password,
      } : undefined,
      child: data.childName ? {
        name: data.childName,
        dob: data.childDob!,
        gender: data.childGender!,
      } : undefined,
      childId: data.childId,
      assessment: {
        scorePhysical: scores.physical?.percentage || 0,
        scoreCognitive: scores.cognitive?.percentage || 0,
        scoreSocial: scores.social?.percentage || 0,
        ageGroupId: ageGroupId,
        checkedSkills: checkedSkillsList,
      },
    });
  };

  const handlePrint = () => {
    if (isPreviewMode) {
      setShowLeadModal(true);
    } else {
      window.print();
    }
  };

  const handleEmail = async () => {
    if (isPreviewMode) {
      setShowLeadModal(true);
      return;
    }

    const token = localStorage.getItem("parent_token");
    if (!token) {
      setEmailStatus({
        type: "error",
        message: t("loginRequiredToEmailReport")
      });
      return;
    }

    setEmailLoading(true);
    setEmailStatus(null);
    try {
      await emailReport(reportId!, i18n.language);
      setEmailStatus({
        type: "success",
        message: t("reportEmailSentSuccess")
      });
    } catch (err: any) {
      console.error(err);
      setEmailStatus({
        type: "error",
        message: t("reportEmailSendFailed")
      });
    } finally {
      setEmailLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF7]">
        <div className="text-center text-gray-500">{t("loading")}</div>
      </div>
    );
  }

  if (hasError || !report || !ageGroupId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF7]">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-gray-400 mb-4" />
          <p className="text-gray-500">{t("reportNotFound")}</p>
          <button
            onClick={() => navigate("/")}
            className="mt-4 px-6 py-2 bg-primary-400 text-white rounded-full text-sm font-medium"
          >
            {t("back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF7] pt-20 md:pt-24 pb-8">
      <div className="w-full px-4 md:px-8 lg:px-16 max-w-5xl mx-auto">
        
        {/* Banner Alert for Preview Mode */}
        {isPreviewMode && (
          <div className="bg-primary-50 border border-primary-200 rounded-2xl p-5 mb-8 flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 text-xl flex-shrink-0 animate-pulse">
                <i className="ri-information-line" />
              </div>
              <div className="text-start">
                <p className="text-sm font-bold text-navy-400">
                  {t("temporaryReportTitle")}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                  {t("temporaryReportHint")}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowLeadModal(true)}
              className="px-6 py-2.5 bg-primary-400 hover:bg-primary-500 text-white rounded-full font-semibold text-xs md:text-sm transition-all hover:scale-105 shadow-md flex items-center gap-1.5 whitespace-nowrap"
            >
              <i className="ri-save-line" />
              {t("saveReportRegisterChild")}
            </button>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <div className="w-16 h-16 rounded-full bg-primary-400/10 flex items-center justify-center mx-auto mb-4">
            <i className="ri-bar-chart-grouped-line text-primary-400 text-2xl" />
          </div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-navy-400 mb-2">{t("reportTitle")}</h1>
          <p className="text-sm md:text-base text-gray-500">
            {t("reportSubtitle")}{" "}
            <span className="font-semibold text-navy-400">
              {ageGroup ? `${ageGroup.title} ${ageRangeLabel}` : ""}
            </span>
          </p>
        </div>

        {/* Overall Score Card */}
        <div className="bg-white rounded-2xl p-5 md:p-8 border border-gray-100 shadow-sm mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
            <div className="relative w-28 h-28 md:w-36 md:h-36 flex-shrink-0">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#f0f0f0" strokeWidth="8" />
                <circle
                  cx="50" cy="50" r="42"
                  fill="none"
                  stroke={overallStatus.color}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${overallPercentage * 2.64} 264`}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl md:text-3xl font-bold text-navy-400">{overallPercentage}%</span>
                <span className="text-xs text-gray-500">{t("developmentStatus")}</span>
              </div>
            </div>
            <div className="flex-1 text-center md:text-start">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-3"
                style={{ backgroundColor: `${overallStatus.color}15`, color: overallStatus.color }}
              >
                <i className="ri-shield-check-line" />
                {overallStatus.label}
              </div>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                {t("assessedSummary", {
                  checked: checkedSkillsList.length,
                  total: ageSkills.length,
                  status: overallStatus.label,
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Development Bars - 3 Bars with different colors */}
        <div className="bg-white rounded-2xl p-5 md:p-8 border border-gray-100 shadow-sm mb-6 md:mb-8">
          <h2 className="text-lg md:text-xl font-bold text-navy-400 mb-6">{t("developmentOverview")}</h2>
          <div className="space-y-5 md:space-y-6">
            {categories.map((cat) => {
              const ui = getCategoryUi(cat.key);
              const data = scores[cat.key];
              if (!data) return null;
              const status = getStatus(data.percentage);

              return (
                <DevelopmentBar
                  key={cat.id}
                  label={cat.name}
                  percentage={data.percentage}
                  score={data.score}
                  total={data.total}
                  color={cat.colorHex}
                  status={status.label}
                  statusColor={status.color}
                  iconUrl={cat.iconUrl}
                  fallbackIcon={ui.icon}
                />
              );
            })}
          </div>
        </div>

        {/* Category-Grouped Skills & Recommendations */}
        <div className="space-y-8 mb-8">
          {categories.map((cat) => {
            const ui = getCategoryUi(cat.key);
            const data = scores[cat.key];
            if (!data) return null;

            const status = getStatus(data.percentage);
            const missing = missingSkillsByCategory[cat.key] || [];
            const mastered = masteredSkillsByCategory[cat.key] || [];

            return (
              <div key={cat.id} className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm text-start">
                {/* Category Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-5 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${cat.colorHex}15` }}>
                      <IconRenderer iconUrl={cat.iconUrl} fallbackIcon={ui.icon} color={cat.colorHex} size={22} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-navy-400">{cat.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full" style={{ backgroundColor: `${status.color}15`, color: status.color }}>
                          {status.label}
                        </span>
                        <span className="text-xs text-gray-400">
                          {data.score} {t("of")} {data.total} {t("skills")}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="flex items-center gap-3">
                    <div className="w-36 md:w-48 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{ width: `${data.percentage}%`, backgroundColor: cat.colorHex }}
                      />
                    </div>
                    <span className="text-sm font-bold text-navy-400">{data.percentage}%</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Mastered Skills */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-bold text-green-600 flex items-center gap-1.5 pb-2 border-b border-green-50">
                      <i className="ri-checkbox-circle-line text-green-500 text-base" />
                      {t("masteredSkillsTitle")} ({mastered.length})
                    </h4>
                    {mastered.length === 0 ? (
                      <p className="text-xs text-gray-400 italic py-2">
                        {t("noMasteredSkillsInCategory")}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {mastered.map((skill) => (
                          <div key={skill.id} className="flex items-start gap-2.5 p-3 bg-green-50/15 border border-green-100/20 rounded-xl">
                            <i className="ri-check-line text-green-500 text-sm mt-0.5 flex-shrink-0" />
                            <p className="text-xs md:text-sm text-gray-700 leading-relaxed">{skill.title}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Skills to Develop / Missing Skills */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-bold text-red-500 flex items-center gap-1.5 pb-2 border-b border-red-50">
                      <i className="ri-error-warning-line text-red-400 text-base" />
                      {t("skillsToDevelop")} ({missing.length})
                    </h4>
                    {missing.length === 0 ? (
                      <p className="text-xs text-green-600 font-semibold py-2 flex items-center gap-1">
                        <i className="ri-sparkling-2-line text-sm" />
                        {t("allSkillsMasteredInCategory")}
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {missing.map((skill) => {
                          const skillProducts = getProductsForSkill(skill.id, cat.id);

                          return (
                            <div key={skill.id} className="p-3.5 bg-red-50/10 border border-red-100/10 rounded-xl space-y-3">
                              <div className="flex items-start gap-2.5">
                                <i className="ri-close-line text-red-400 text-sm mt-0.5 flex-shrink-0" />
                                <p className="text-xs md:text-sm text-gray-800 font-medium leading-relaxed">{skill.title}</p>
                              </div>
                              
                              {skillProducts.length > 0 && (
                                <div className="pt-2.5 border-t border-gray-100/50">
                                  <p className="text-[10px] font-bold text-gray-500 mb-2 flex items-center gap-1">
                                    <i className="ri-lightbulb-flash-line text-primary-400 text-xs" />
                                    {t("suggestedProducts")}
                                  </p>
                                  <div className="grid grid-cols-1 gap-3">
                                    {skillProducts.slice(0, 2).map((product) => (
                                      <SkillProductCard
                                        key={product.id}
                                        product={product}
                                        selectedQuantity={cartQuantities[product.id] || 0}
                                        onAdd={() => addProductToCart(product)}
                                        onQuantityChange={(quantity) => setProductQuantity(product, quantity)}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Product Selection */}
        {recommendedProducts.length > 0 && (
          <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
              <div className="text-start">
                <h2 className="text-lg md:text-xl font-bold text-navy-400">
                  {t("suggestedProductsCart")}
                </h2>
                <p className="text-xs md:text-sm text-gray-500 mt-1 leading-relaxed max-w-2xl">
                  {t("suggestedProductsCartHint")}
                </p>
              </div>
              <button
                type="button"
                onClick={addAllRecommendedProducts}
                className="h-12 px-5 bg-primary-400 hover:bg-primary-500 text-white rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2"
              >
                <i className="ri-shopping-cart-2-line text-base" />
                {t("addAllProducts")}
              </button>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-3 md:p-4">
              {selectedCartItems.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="w-14 h-14 mx-auto rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-300 mb-3">
                    <i className="ri-shopping-cart-line text-2xl" />
                  </div>
                  <p className="text-sm font-semibold text-navy-400">
                    {t("cartEmpty")}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("cartEmptyHint")}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedCartItems.map(({ product, quantity, stockLimit }) => {
                    const options = productOptions[product.id] || [];
                    const isLoadingOptions = Boolean(productOptionsLoading[product.id]);
                    const selectedOptions = cartSelectedOptions[product.id] || {};

                    return (
                    <div
                      key={product.id}
                      className="bg-white rounded-2xl border border-gray-100 p-3 flex flex-col md:flex-row md:items-center gap-3 text-start"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex-shrink-0">
                            <img
                              src={product.imageUrl}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-navy-400 line-clamp-2">{product.name}</p>
                            <p className="text-[11px] text-gray-500 mt-1">
                              {product.price} {t("sar")} - {t("productAvailable")}: {product.stockQuantity}
                            </p>
                          </div>
                        </div>

                        {isLoadingOptions && (
                          <p className="text-[11px] text-gray-400 mt-2">
                            {t("loadingProductOptions")}
                          </p>
                        )}

                        {options.length > 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                            {options.map((option) => (
                              <label key={option.id} className="block">
                                <span className="block text-[11px] font-semibold text-navy-400 mb-1">
                                  {option.name}
                                  {option.required && <span className="text-primary-400"> *</span>}
                                </span>
                                <select
                                  value={selectedOptions[option.id] || ""}
                                  onChange={(event) => setProductOptionValue(product.id, option.id, event.target.value)}
                                  className="w-full h-10 rounded-xl border border-gray-200 bg-white px-3 text-xs text-navy-400 outline-none focus:border-primary-300"
                                  aria-label={option.name}
                                >
                                  <option value="">{t("productOptionPlaceholder")}</option>
                                  {option.values
                                    .filter((value) => !value.isOutOfStock)
                                    .map((value) => (
                                      <option key={value.id} value={value.id}>
                                        {value.name}
                                      </option>
                                    ))}
                                </select>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between md:justify-end gap-3">
                        <div className="h-10 rounded-xl border border-gray-200 bg-white flex items-center overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setProductQuantity(product, quantity - 1)}
                            className="w-9 h-full text-navy-400 flex items-center justify-center hover:bg-gray-50"
                            aria-label={t("decreaseQuantity")}
                            title={t("decreaseQuantity")}
                          >
                            <i className="ri-subtract-line text-sm" />
                          </button>
                          <input
                            type="number"
                            min={0}
                            max={stockLimit}
                            value={quantity}
                            onChange={(event) => setProductQuantity(product, Number(event.target.value))}
                            className="w-12 h-full border-x border-gray-100 text-center text-sm font-bold text-navy-400 outline-none"
                            aria-label={t("productQuantity")}
                          />
                          <button
                            type="button"
                            onClick={() => setProductQuantity(product, quantity + 1)}
                            disabled={quantity >= stockLimit}
                            className="w-9 h-full text-navy-400 flex items-center justify-center hover:bg-gray-50 disabled:opacity-35"
                            aria-label={t("increaseQuantity")}
                            title={t("increaseQuantity")}
                          >
                            <i className="ri-add-line text-sm" />
                          </button>
                        </div>

                        <div className="w-24 text-end">
                          <p className="text-sm font-bold text-primary-400">
                            {(product.price * quantity).toFixed(2)} {t("sar")}
                          </p>
                          <p className="text-[10px] text-gray-400">
                            {quantity} x {product.price}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => setProductQuantity(product, 0)}
                          className="h-10 w-10 rounded-xl border border-red-100 bg-white text-red-500 flex items-center justify-center hover:bg-red-50"
                          aria-label={t("removeProductFromCart")}
                          title={t("removeProductFromCart")}
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </div>
                  );
                  })}

                  {checkoutRedirectMode === "salla_order" && (
                    <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-4 text-start">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-bold text-navy-400">{t("checkoutShippingAddress")}</h3>
                          <p className="text-[11px] text-gray-500 mt-1">{t("checkoutShippingAddressHint")}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowNewCheckoutAddress((current) => !current)}
                          className="h-10 px-4 rounded-xl border border-gray-200 text-navy-400 text-xs font-bold hover:border-primary-300"
                        >
                          <i className="ri-add-line me-1" />
                          {t("checkoutAddNewAddress")}
                        </button>
                      </div>

                      {checkoutAddresses.length > 0 && !showNewCheckoutAddress && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {checkoutAddresses.map((address) => (
                            <label
                              key={address.id}
                              className={`rounded-2xl border p-3 cursor-pointer transition-all ${
                                selectedCheckoutAddressId === address.id
                                  ? "border-primary-300 bg-primary-50/20"
                                  : "border-gray-100 bg-gray-50/60 hover:border-gray-200"
                              }`}
                            >
                              <input
                                type="radio"
                                name="checkout-address"
                                value={address.id}
                                checked={selectedCheckoutAddressId === address.id}
                                onChange={() => setSelectedCheckoutAddressId(address.id)}
                                className="sr-only"
                              />
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className="text-sm font-bold text-navy-400">
                                    {address.label || address.recipientName || t("checkoutSavedAddress")}
                                  </p>
                                  <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
                                    {address.address}
                                    {address.addressLine ? ` - ${address.addressLine}` : ""}
                                    {address.districtName ? ` - ${address.districtName}` : ""}
                                  </p>
                                  <p className="text-[11px] text-gray-400 mt-1">
                                    {address.phoneNumber}
                                  </p>
                                </div>
                                {address.isDefault && (
                                  <span className="text-[10px] text-primary-500 bg-primary-50 px-2 py-1 rounded-full">
                                    {t("checkoutDefaultAddress")}
                                  </span>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}

                      {showNewCheckoutAddress && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                            <input
                              ref={checkoutAddressSearchRef}
                              value={checkoutAddressSearch}
                              onChange={(event) => setCheckoutAddressSearch(event.target.value)}
                              placeholder={googleMapsApiKey ? t("checkoutAddressSearch") : t("checkoutAddressSearchDisabled")}
                              disabled={!googleMapsApiKey}
                              className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300 disabled:bg-gray-50"
                            />
                            <button
                              type="button"
                              onClick={handleUseCurrentLocation}
                              disabled={checkoutLocationLoading}
                              className="h-11 rounded-xl border border-primary-200 px-4 text-sm font-bold text-primary-500 hover:bg-primary-50 disabled:opacity-60"
                            >
                              {checkoutLocationLoading ? t("checkoutLocationLoading") : t("checkoutUseCurrentLocation")}
                            </button>
                          </div>
                          <input
                            value={checkoutAddressForm.label || ""}
                            onChange={(event) => updateCheckoutAddressForm("label", event.target.value)}
                            placeholder={t("checkoutAddressLabel")}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300"
                          />
                          <input
                            value={checkoutAddressForm.recipientName || ""}
                            onChange={(event) => updateCheckoutAddressForm("recipientName", event.target.value)}
                            placeholder={t("checkoutRecipientName")}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300"
                          />
                          <input
                            value={checkoutAddressForm.phoneNumber || ""}
                            onChange={(event) => updateCheckoutAddressForm("phoneNumber", event.target.value)}
                            placeholder={t("checkoutRecipientPhone")}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300"
                          />
                          <select
                            value={checkoutAddressForm.countryId}
                            onChange={(event) => updateCheckoutAddressForm("countryId", event.target.value)}
                            disabled={checkoutCountriesLoading || checkoutCountries.length === 0}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300 disabled:bg-gray-50"
                            aria-label={t("checkoutCountry")}
                          >
                            <option value="">
                              {checkoutCountriesLoading ? t("checkoutLocationsLoading") : t("checkoutCountry")}
                            </option>
                            {checkoutCountries.map((country) => (
                              <option key={country.id} value={country.id}>
                                {isRTL ? country.name : country.nameEn}
                              </option>
                            ))}
                          </select>
                          <select
                            value={checkoutAddressForm.cityId}
                            onChange={(event) => updateCheckoutAddressForm("cityId", event.target.value)}
                            disabled={checkoutCitiesLoading || checkoutCities.length === 0}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300 disabled:bg-gray-50"
                            aria-label={t("checkoutCity")}
                          >
                            <option value="">
                              {checkoutCitiesLoading ? t("checkoutLocationsLoading") : t("checkoutCity")}
                            </option>
                            {checkoutCities.map((city) => (
                              <option key={city.id} value={city.id}>
                                {isRTL ? city.name : city.nameEn}
                              </option>
                            ))}
                          </select>
                          <select
                            value={checkoutAddressForm.districtId || ""}
                            onChange={(event) => updateCheckoutDistrict(event.target.value)}
                            disabled={checkoutDistrictsLoading || checkoutDistricts.length === 0}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300 disabled:bg-gray-50"
                            aria-label={t("checkoutDistrict")}
                          >
                            <option value="">
                              {checkoutDistrictsLoading ? t("checkoutLocationsLoading") : t("checkoutDistrict")}
                            </option>
                            {checkoutDistricts.map((district) => (
                              <option key={district.id} value={district.id}>
                                {isRTL ? district.name : district.nameEn}
                              </option>
                            ))}
                          </select>
                          <input
                            value={checkoutAddressForm.districtName || ""}
                            onChange={(event) => updateCheckoutAddressForm("districtName", event.target.value)}
                            placeholder={t("checkoutDistrictName")}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300"
                          />
                          <input
                            value={checkoutAddressForm.address}
                            onChange={(event) => updateCheckoutAddressForm("address", event.target.value)}
                            placeholder={t("checkoutAddressLine1")}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300 md:col-span-2"
                          />
                          <input
                            value={checkoutAddressForm.addressLine || ""}
                            onChange={(event) => updateCheckoutAddressForm("addressLine", event.target.value)}
                            placeholder={t("checkoutAddressDetails")}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300"
                          />
                          <input
                            value={checkoutAddressForm.streetNumber || ""}
                            onChange={(event) => updateCheckoutAddressForm("streetNumber", event.target.value)}
                            placeholder={t("checkoutStreetNumber")}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300"
                          />
                          <input
                            value={checkoutAddressForm.geoCoordinates || ""}
                            onChange={(event) => updateCheckoutAddressForm("geoCoordinates", event.target.value)}
                            placeholder={t("checkoutGeoCoordinates")}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300"
                          />
                          <input
                            value={checkoutAddressForm.postalCode || ""}
                            onChange={(event) => updateCheckoutAddressForm("postalCode", event.target.value)}
                            placeholder={t("checkoutPostalCode")}
                            className="h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-300"
                          />
                          <button
                            type="button"
                            onClick={handleSaveCheckoutAddress}
                            className="h-11 rounded-xl bg-primary-400 text-white font-bold text-sm hover:bg-primary-500 md:col-span-2"
                          >
                            {t("checkoutSaveAddress")}
                          </button>
                        </div>
                      )}

                      {selectedCheckoutAddress && (
                        <div>
                          <label className="block">
                            <span className="block text-xs font-bold text-navy-400 mb-2">
                              {t("checkoutShippingCompany")}
                            </span>
                            <select
                              value={selectedShippingRateId}
                              onChange={(event) => setSelectedShippingRateId(event.target.value)}
                              disabled={shippingRatesLoading || shippingRates.length === 0}
                              className="w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-400 outline-none focus:border-primary-300 disabled:bg-gray-50"
                            >
                              <option value="">
                                {shippingRatesLoading ? t("shippingRatesLoading") : t("shippingRateRequired")}
                              </option>
                              {shippingRates.map((rate) => (
                                <option key={rate.id} value={rate.id}>
                                  {rate.title} - {rate.amount.toFixed(2)} {rate.currency || t("sar")}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="text-start">
                      <p className="text-xs text-gray-500">
                        {t("cartPieces")}: <strong className="text-navy-400">{selectedItemsCount}</strong>
                      </p>
                      {displayedShippingCost > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t("cartShipping")}:
                          <strong className="text-navy-400">
                            {" "}{displayedShippingCost.toFixed(2)} {t("sar")}
                          </strong>
                          {selectedShippingRate?.title && (
                            <span className="text-gray-400"> - {selectedShippingRate.title}</span>
                          )}
                        </p>
                      )}
                      <p className="text-lg font-bold text-navy-400 mt-1">
                        {t("cartTotal")}: {selectedGrandTotal.toFixed(2)} {t("sar")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleCheckout}
                      disabled={checkoutLoading}
                      className="h-12 px-6 bg-navy-400 hover:bg-navy-500 disabled:bg-gray-300 text-white rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2"
                    >
                      {checkoutLoading ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <i className="ri-secure-payment-line text-base" />
                      )}
                      {checkoutRedirectMode === "manual_payment_request"
                        ? t("checkoutCreatePaymentRequest")
                        : checkoutRedirectMode === "salla_order"
                          ? t("checkoutCreateSallaOrder")
                          : t("checkoutInStore", { storeName: checkoutStoreName || t("sallaStoreFallback") })}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {checkoutError && (
              <div className="mt-4 p-3 rounded-xl border border-red-100 bg-red-50 text-red-600 text-xs flex items-center gap-2">
                <i className="ri-error-warning-line text-sm" />
                <span>{checkoutError}</span>
              </div>
            )}
            {checkoutSuccess && (
              <div className="mt-4 p-3 rounded-xl border border-green-100 bg-green-50 text-green-700 text-xs flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-sm" />
                <span>{checkoutSuccess}</span>
              </div>
            )}
            <p className="text-[11px] text-gray-400 mt-3 text-center">
              {checkoutRedirectMode === "manual_payment_request"
                ? t("manualPaymentRequestMessage")
                : checkoutRedirectMode === "salla_order"
                  ? t("sallaOrderRequestMessage")
                  : t("secureCheckoutMessage")}
            </p>
          </div>
        )}

        {emailStatus && (
          <div className={`mb-6 max-w-md mx-auto p-4 rounded-2xl border text-sm text-center flex items-center justify-center gap-2 ${
            emailStatus.type === "success" 
              ? "bg-green-50 border-green-200 text-green-700" 
              : "bg-red-50 border-red-200 text-red-700"
          }`}>
            <i className={emailStatus.type === "success" ? "ri-checkbox-circle-line text-lg" : "ri-error-warning-line text-lg"} />
            <span>{emailStatus.message}</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center pb-8">
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-primary-400 hover:bg-primary-500 text-white rounded-full font-semibold text-sm transition-all hover:scale-105 flex items-center justify-center gap-2"
          >
            <i className="ri-refresh-line" />
            {t("retakeAssessment")}
          </button>
          <button
            onClick={handlePrint}
            className="px-8 py-3 bg-white border-2 border-gray-200 hover:border-primary-400 text-navy-400 rounded-full font-semibold text-sm transition-all flex items-center justify-center gap-2"
          >
            <i className="ri-printer-line" />
            {t("printReport")}
          </button>
          <button
            onClick={handleEmail}
            disabled={emailLoading}
            className="px-8 py-3 bg-white border-2 border-gray-200 hover:border-primary-400 text-navy-400 rounded-full font-semibold text-sm transition-all flex items-center justify-center gap-2 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-200"
          >
            {emailLoading ? (
              <div className="w-4 h-4 border-2 border-navy-400/30 border-t-navy-400 rounded-full animate-spin" />
            ) : (
              <i className="ri-mail-send-line" />
            )}
            {t("sendViaEmail")}
          </button>
        </div>
      </div>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSubmit={handleLeadSubmit}
      />
    </div>
  );
}
